'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Company {
  id: string;
  name: string;
  slug: string;
  logo_url?: string | null;
}

interface TenantContextValue {
  company: Company | null;
  companyId: string | null;
  loading: boolean;
  refreshCompany: () => void;
}

const TenantContext = createContext<TenantContextValue>({
  company: null,
  companyId: null,
  loading: true,
  refreshCompany: () => {},
});

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }: { children: React.ReactNode }) => {
  const { profile, loading: authLoading } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const supabase = createClient();

  const refreshCompany = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (authLoading) return;

    if (!profile?.company_id) {
      setCompany(null);
      setLoading(false);
      return;
    }

    async function fetchCompany() {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('id, name, slug, logo_url')
          .eq('id', profile!.company_id!)
          .single();

        if (!error && data) {
          setCompany(data as Company);
        } else {
          setCompany(null);
        }
      } catch {
        setCompany(null);
      } finally {
        setLoading(false);
      }
    }

    fetchCompany();
  }, [profile?.company_id, authLoading, refreshKey]);

  return (
    <TenantContext.Provider
      value={{
        company,
        companyId: company?.id ?? null,
        loading,
        refreshCompany,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
